import { Box, ButtonBase, makeStyles, Typography } from "@material-ui/core";
import React from "react";

import { Chevron } from "@/components/Chevron";
import { homeCardStyle } from "@/components/home/HomeSection";
import { LimitedPropertyWithAgent } from "@/components/property/LimitedPropertiesSection";
import { layoutTokens as layout, semanticColors as colors, textStyles } from "@/designTokens";
import theme from "@/theme";
import { calculateYieldRate } from "@/utilities/yield";

import NoImage from "@assets/image/NoImage.svg";

const useStyles = makeStyles({
  card: {
    ...homeCardStyle,
    borderRadius: layout.radius.md,
    display: "flex",
    width: "100%",
    alignItems: "stretch",
    textAlign: "left",
    overflow: "hidden",
    minWidth: 0,
    [theme.breakpoints.up("md")]: { flexDirection: "column", borderRadius: layout.radius.lg },
  },
  image: {
    width: 112,
    flexShrink: 0,
    objectFit: "cover",
    backgroundColor: colors["field/bg"],
    alignSelf: "stretch",
    [theme.breakpoints.up("md")]: { width: "100%", height: 200 },
  },
  content: {
    display: "flex",
    flex: 1,
    minWidth: 0,
    gap: layout.space[12],
    padding: layout.space[12],
    alignItems: "center",
  },
  texts: {
    display: "flex",
    flexDirection: "column",
    gap: 2,
    flex: 1,
    minWidth: 0,
    [theme.breakpoints.up("md")]: { gap: layout.space[4] },
  },
  priceRow: { display: "flex", alignItems: "baseline", gap: layout.space[4], width: "100%", flexWrap: "wrap" },
  price: { ...textStyles["Number/S"], [theme.breakpoints.up("md")]: textStyles["Number/M"] },
  small: {
    ...textStyles["SP/Body/Small"],
    color: colors["text/secondary"],
    [theme.breakpoints.up("md")]: textStyles["PC/Body/Small"],
  },
  yield: { marginLeft: "auto", ...textStyles["SP/Body/Small"], color: colors["text/secondary"] },
  title: {
    ...textStyles["SP/Button/M"],
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    [theme.breakpoints.up("md")]: textStyles["PC/Heading/H4"],
  },
  condition: {
    [theme.breakpoints.up("md")]: textStyles["PC/Body/Small"],
    ...textStyles["SP/Body/Small"],
    color: colors["text/secondary"],
    overflow: "hidden",
    whiteSpace: "nowrap",
    textOverflow: "ellipsis",
  },
});

export const formatLimitedPrice = (value?: number | null) =>
  value == null
    ? "-"
    : (value / 10000).toLocaleString(undefined, {
        maximumFractionDigits: Number.isInteger(value / 10000) ? 0 : 1,
        minimumFractionDigits: Number.isInteger(value / 10000) ? 0 : 1,
      });

export const LimitedPropertyCard: React.FC<{ property: LimitedPropertyWithAgent; onClick: () => void }> = ({
  property,
  onClick,
}) => {
  const classes = useStyles();
  return (
    <ButtonBase
      className={classes.card}
      onClick={onClick}
      aria-label={`${property.property_name || "物件"}の詳細を見る`}
    >
      <img
        className={classes.image}
        src={property.file_url || property.object_storage_path || NoImage}
        alt=""
        loading="lazy"
      />
      <Box className={classes.content}>
        <Box className={classes.texts}>
          <Box className={classes.priceRow}>
            <Typography component="span" className={classes.price}>
              {formatLimitedPrice(property.property_price)}
            </Typography>
            {property.property_price != null && (
              <Typography component="span" className={classes.small}>
                万円
              </Typography>
            )}
            <Typography component="span" className={classes.yield}>
              利回り {calculateYieldRate(property.property_monthly_rent, property.property_price)}
            </Typography>
          </Box>
          <Typography data-testid="limited-property-name" className={classes.title}>
            {property.property_name || "-"}
          </Typography>
          <Typography
            className={classes.condition}
            title={[property.property_area, property.property_nearest_station].filter(Boolean).join(" ・ ")}
          >
            {[property.property_area, property.property_nearest_station].filter(Boolean).join(" ・ ") || "-"}
          </Typography>
          <Typography className={classes.condition}>
            家賃 {property.property_monthly_rent == null ? "-" : `${property.property_monthly_rent.toLocaleString()}円`}
          </Typography>
        </Box>
        <Chevron style={{ fontSize: 12 }} />
      </Box>
    </ButtonBase>
  );
};
